import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";

// Rename a single layer. Mirrors the SetLayerPropertyCommand pattern but is
// kept as its own command for clearer history entries ("Rename layer" vs.
// "Set layer property"). Callers that want to batch property changes should
// keep using SetLayerPropertyCommand.

export class RenameLayerCommand implements Command {
  readonly kind = "rename-layer";
  constructor(
    private readonly layerId: string,
    private readonly newName: string,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: rename(doc.layers, this.layerId, this.newName) };
  }

  invert(before: Document, _after: Document): Command {
    const original = findLayer(before.layers, this.layerId);
    if (!original) return new RenameLayerCommand(this.layerId, this.newName);
    return new RenameLayerCommand(this.layerId, original.name);
  }
}

function rename(layers: readonly Layer[], id: string, name: string): Layer[] {
  return layers.map((l) => {
    if (l.id === id) return { ...l, name };
    if (l.kind === "group") {
      const group: GroupLayer = { ...l, children: rename(l.children, id, name) };
      return group;
    }
    return l;
  });
}

function findLayer(layers: readonly Layer[], id: string): Layer | null {
  for (const l of layers) {
    if (l.id === id) return l;
    if (l.kind === "group") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
